import { SyntheticEvent as Event, SyntheticEvent, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import Form from 'react-bootstrap/Form';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { DashboardHoneyCombColorType, GraphConfigType } from 'components/dashboards/types';
import { DEFAULT_GRAPH_STYLE, getGraphTitle } from 'components/dashboards/dashboardData';
import { ADD_GRAPH_TO_DASHBOARD, EDIT_DASHBOARD_GRAPH, GET_CONFIGV2 } from 'components/dashboards/queries';

import Link from 'components/common/Link/Link';
import Dialog from 'components/common/Dialog/Dialog';
import Select from 'components/common/ReactSelect/Select';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';

import * as K from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';

import './EditPanel.scss';

/**
 * Modal dialog for editing a non-timeseries visualization.
 *
 * @prop showModal          This modal is shown when true
 * @prop dashboardName      The dashboard config file name
 * @prop panelIndex         The index for this panel (-1 for a new panel)
 * @prop incomingConfig     The config for this panel
 * @prop handleClose        Callback for when the modal is closed
 *
 */

type EditPanelProps = {
  showModal: boolean;
  dashboardName?: string;
  panelIndex: number;
  incomingConfig?: GraphConfigType;
  shouldSave?: (panelConfig?: GraphConfigType) => boolean;
  handleClose: () => void;
};

type PanelOption = {
  label: string;
  value: string;
  tip?: string;
};

function EditPanel({
  showModal,
  dashboardName,
  panelIndex,
  incomingConfig,
  shouldSave = (): boolean => true,
  handleClose,
}: EditPanelProps): JSX.Element {
  const isNewPanel = panelIndex < 0;
  const panelConfig: GraphConfigType = !isNewPanel
    ? (incomingConfig as GraphConfigType)
    : {
        graphStyle: DEFAULT_GRAPH_STYLE,
        title: '',
        query: '',
        ...incomingConfig,
      };

  const isK8sDashboard = useIsLabEnabled(K.LAB.K8S_DASHBOARD);

  const PANEL_TYPE_OPTIONS = [
    { label: 'Pie Chart', value: 'pie', tip: ' - use a filter that returns a text column and a numeric column' },
    { label: 'Donut Chart', value: 'donut' },
    { label: 'Table Panel', value: 'table' },
    { label: 'Number Panel', value: 'number', tip: ' - use a filter that returns a single numeric column' },
    { label: 'Markdown Panel', value: 'markdown', tip: ' - supports Github Flavored Markdown' },
  ];

  if (isK8sDashboard) {
    PANEL_TYPE_OPTIONS.push({
      label: 'Honeycomb Chart',
      value: K.PANEL_TYPE.HONEYCOMB,
      tip: ' - use a filter that returns a text column and a numeric column',
    });
  }

  const [panelTitle, setPanelTitle] = useState(getGraphTitle(panelConfig));
  const [panelType, setPanelType] = useState(!panelConfig.graphStyle ? K.PANEL_TYPE.TABLE : panelConfig.graphStyle);
  const [panelQuery, setPanelQuery] = useState(panelConfig.query);
  const [panelMarkdown, setPanelMarkdown] = useState(
    panelConfig.markdown ? panelConfig.markdown.split('\\n').join('\n') : undefined,
  );
  const [queryTip, setQueryTip] = useState(PANEL_TYPE_OPTIONS.find((option) => option.value === panelType));
  const [maxPieSlices, setMaxPieSlices] = useState(panelConfig.maxPieSlices || K.DEFAULT_MAX_PIE_SLICES);
  const [honeyCombThresholds, setHoneyCombThresholds] = useState(
    panelConfig.honeyCombThresholds ? panelConfig.honeyCombThresholds : K.DEFAULT_HONEYCOMB_THRESHOLD_VALUES,
  );
  const [honeyCombColor, setHoneyCombColor] = useState(
    panelConfig.honeyCombColor ? panelConfig.honeyCombColor : K.HONEYCOMB_COLOR_OPTIONS[0],
  );
  const [honeyCombGroupby, setHoneyCombGroupBy] = useState(
    panelConfig.honeyCombGroupBy ? panelConfig.honeyCombGroupBy : '',
  );
  const [honeyCombLinkTo, setHoneyCombLinkTo] = useState(
    panelConfig.honeyCombLinkTo ? panelConfig.honeyCombLinkTo : '',
  );
  const [isGroupBy, setGroupBy] = useState(panelConfig.honeyCombGroupBy ? true : false);
  const [isLinkTo, setLinkTo] = useState(panelConfig.honeyCombLinkTo ? true : false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showBarsColumn, setShowBarsColumn] = useState(panelConfig.showBarsColumn === 'true' ? true : false);

  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [savePanelToDashboard] = useMutation(isNewPanel ? ADD_GRAPH_TO_DASHBOARD : EDIT_DASHBOARD_GRAPH, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const savePanel = (): void => {
    const newPanelConfig = {
      ...panelConfig,
      graphStyle: panelType === K.PANEL_TYPE.TABLE ? '' : panelType,
      query: panelQuery,
      markdown: panelMarkdown,
      title: panelTitle,
    };

    if (panelType === K.PANEL_TYPE.DONUT || panelType === K.PANEL_TYPE.PIE) {
      newPanelConfig.maxPieSlices = maxPieSlices;
    }

    if (panelType === K.PANEL_TYPE.TABLE) {
      newPanelConfig.showBarsColumn = showBarsColumn ? 'true' : 'false';
    }

    if (panelType === K.PANEL_TYPE.HONEYCOMB) {
      newPanelConfig.honeyCombColor = honeyCombColor;
      newPanelConfig.honeyCombThresholds = honeyCombThresholds;
      if (isGroupBy && honeyCombGroupby) {
        newPanelConfig.honeyCombGroupBy = honeyCombGroupby;
      } else {
        newPanelConfig.honeyCombGroupBy = '';
      }
      if (isLinkTo && honeyCombLinkTo) {
        newPanelConfig.honeyCombLinkTo = honeyCombLinkTo;
      } else {
        newPanelConfig.honeyCombLinkTo = '';
      }
    }
    const variables = {
      dashboardName,
      graphIndex: panelIndex,
      graph: JSON.stringify(newPanelConfig),
      tabName,
    };
    savePanelToDashboard({ variables })
      .then(() => {
        handleClose();
      })
      .catch((error) => {
        setPageAlertConfig({
          message: clearGraphQLErrorMessage(error.message),
          variant: PageAlertVariant.ERROR,
          autoHide: true,
        });
      });
  };

  const handleOK = (): void => {
    if (shouldSave(panelConfig)) {
      savePanel();
    }
  };

  const onChangeThresholds = (event: Event, idx: number): void => {
    let newThreshold = [...honeyCombThresholds];
    newThreshold[idx] = (event.target as HTMLInputElement).value;
    setHoneyCombThresholds(newThreshold);
  };

  const getEventString = (event: Event): string => (event.target as HTMLInputElement).value;

  const honeyCombAdvancedSettings = (
    <>
      <Form.Group>
        <Form.Label>Color</Form.Label>
        <Select
          options={K.HONEYCOMB_COLOR_OPTIONS}
          onChange={(selectedOption) => {
            setHoneyCombColor(selectedOption as DashboardHoneyCombColorType);
          }}
          value={K.HONEYCOMB_COLOR_OPTIONS.find((option) => option.value === honeyCombColor.value)}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Thresholds</Form.Label>
        {K.DEFAULT_HONEYCOMB_THRESHOLD_VALUES.map((val: string, idx: number) => (
          <div className="EditPanel__HoneyComb__Thresholds" key={idx}>
            <div
              className="EditPanel__HoneyComb__Hexagon"
              style={{ backgroundColor: honeyCombColor.value, opacity: K.HONEYCOMB_THRESHOLD_OPACITY[idx] }}
            ></div>
            {idx !== 0 ? (
              <div className="EditPanel__HoneyComb__Thresholds__Block">
                <Form.Control
                  type="text"
                  className="HoneyComb__Thresholds"
                  size="sm"
                  onChange={(event: Event) => onChangeThresholds(event, idx)}
                  value={honeyCombThresholds[idx]}
                />
                <span> and above</span>
              </div>
            ) : (
              <div>Default</div>
            )}
          </div>
        ))}
      </Form.Group>
      <Form.Group>
        <Form.Label>
          Grouping -
          <span className="EditPanel__Tip">Show multiple honeycomb charts, one for each value of a field</span>
        </Form.Label>
        <div className="EditPanel__HoneyComb__InputSettings">
          <CheckboxRadio
            type="checkbox"
            checked={isGroupBy}
            value={isGroupBy}
            onChange={() => {
              setGroupBy(!isGroupBy);
              setHoneyCombGroupBy('');
            }}
          />
          <span>GroupBy</span>
          <div className="EditPanel__HoneyComb__Input">
            <Form.Control
              placeholder="Field name"
              type="text"
              onChange={(event: Event): void => setHoneyCombGroupBy(getEventString(event))}
              disabled={!isGroupBy}
              value={honeyCombGroupby}
            />
          </div>
        </div>
        <div className="EditPanel__HoneyComb__Tip">Must be a column in the PowerQuery table</div>
      </Form.Group>
      <Form.Group>
        <Form.Label>
          Linking -<span className="EditPanel__Tip">Link to another page when the user clicks on a honeycomb cell</span>
        </Form.Label>
        <div className="EditPanel__HoneyComb__InputSettings">
          <CheckboxRadio
            type="checkbox"
            checked={isLinkTo}
            value={isLinkTo}
            onChange={() => {
              setLinkTo(!isLinkTo);
              setHoneyCombLinkTo('');
            }}
          />
          <span>Link to</span>
          <div className="EditPanel__HoneyComb__Input">
            <Form.Control
              placeholder="URL"
              type="text"
              onChange={(event: Event): void => setHoneyCombLinkTo(getEventString(event))}
              disabled={!isLinkTo}
              value={honeyCombLinkTo}
            />
          </div>
        </div>
        <div className="EditPanel__HoneyComb__Tip">
          To include columns from the PowerQuery as variables, use the syntax [name] (e.g.,[serverHost])
        </div>
      </Form.Group>
    </>
  );
  return (
    <Dialog
      title={isNewPanel ? `Add ${queryTip?.label || 'Panel'}` : 'Edit Panel'}
      show={showModal}
      close={(): void => {}}
      okCallback={handleOK}
      cancelCallback={handleClose}
    >
      <div className="EditPanel">
        <Form>
          <Form.Group>
            <Form.Group controlId="panelTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                autoComplete="off"
                placeholder="What to call this panel"
                value={panelTitle}
                type="text"
                onChange={(event: Event): void => setPanelTitle(getEventString(event))}
              />
            </Form.Group>

            <Form.Label>Type</Form.Label>
            <Select
              options={PANEL_TYPE_OPTIONS}
              onChange={(selectedOption) => {
                const { value } = selectedOption as PanelOption;
                setPanelType(value);
                setQueryTip(PANEL_TYPE_OPTIONS.find((option) => option.value === value));
              }}
              value={PANEL_TYPE_OPTIONS.find((option) => option.value === panelType)}
            />
          </Form.Group>

          {(panelType === K.PANEL_TYPE.DONUT || panelType === K.PANEL_TYPE.PIE) && (
            <Form.Group>
              <Form.Label>Maximum Slices</Form.Label>
              <Form.Control
                value={maxPieSlices}
                type="number"
                onChange={(event: SyntheticEvent) => {
                  const eventTarget = event.target as HTMLInputElement;
                  setMaxPieSlices(eventTarget.valueAsNumber);
                }}
              />
            </Form.Group>
          )}

          {panelType !== 'markdown' && (
            <Form.Group>
              <Form.Label>
                Filter
                {queryTip?.tip && <span className="EditPanel__Tip">{queryTip.tip}</span>}
              </Form.Label>
              <Form.Control
                placeholder="Search filter text"
                value={panelQuery}
                type="text"
                as="textarea"
                rows={5}
                onChange={(event: Event): void => setPanelQuery(getEventString(event))}
              />
              <div className="EditPanel__Help">
                <Link href="/help/power-queries" openInNewTab>
                  Help with PowerQuery search syntax
                </Link>
              </div>
            </Form.Group>
          )}

          {panelType === 'markdown' && (
            <Form.Group>
              <Form.Label>
                Markdown
                {queryTip?.tip && <span className="EditPanel__Tip">{queryTip.tip}</span>}
              </Form.Label>
              <Form.Control
                placeholder="Markdown content"
                value={panelMarkdown}
                type="text"
                as="textarea"
                rows={10}
                onChange={(event: Event): void => setPanelMarkdown(getEventString(event))}
              />
              <div className="EditPanel__Help">
                <Link href="//guides.github.com/features/mastering-markdown/#examples" openInNewTab>
                  Help with Markdown syntax
                </Link>
              </div>
            </Form.Group>
          )}
          {panelType === K.PANEL_TYPE.TABLE && (
            <Form.Group>
              <Form.Label>
                Data Bars
                <span className="EditPanel__Tip">
                  {' '}
                  - Provides a visual comparison for the last numeric column in the table
                </span>
              </Form.Label>
              <div>
                <CheckboxRadio
                  type="checkbox"
                  label="Include data bars"
                  checked={showBarsColumn}
                  value={showBarsColumn}
                  onChange={() => {
                    setShowBarsColumn(!showBarsColumn);
                  }}
                />
              </div>
            </Form.Group>
          )}
          {panelType === K.PANEL_TYPE.HONEYCOMB && (
            <>
              <Form.Group>
                <div
                  className="EditPanel__HoneyComb__Settings"
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                >
                  {showAdvancedSettings ? (
                    <RemoveCircleOutlineIcon fontSize="small" />
                  ) : (
                    <AddCircleOutlineIcon fontSize="small" />
                  )}
                  <span className="EditPanel__HoneyComb__SettingsLabel">Advanced Settings</span>
                </div>
              </Form.Group>
              {showAdvancedSettings && honeyCombAdvancedSettings}
            </>
          )}
        </Form>
      </div>
    </Dialog>
  );
}

export default EditPanel;
